import { useEffect, useRef } from 'react';
import { LOCAL_FUNCTIONS } from '@/integrations/supabase/growthHubClient';
import { getStoredUtmParams } from '@/components/providers/TrackingProvider';
import { getCookie } from '@/lib/firstPartyCookies';

/**
 * Generates a lightweight browser fingerprint hash.
 * NOT meant to be a full FingerprintJS replacement — just enough
 * to cluster repeat visits from the same device.
 */
const generateFingerprint = async (): Promise<string> => {
  const components: string[] = [];

  // Screen
  components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`);

  // Timezone
  components.push(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Language
  components.push(navigator.language);

  // Platform
  components.push(navigator.platform || '');

  // Hardware concurrency
  components.push(String(navigator.hardwareConcurrency || 0));

  // Device memory (Chrome only)
  components.push(String((navigator as any).deviceMemory || 0));

  // WebGL renderer (GPU)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        components.push((gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '');
      }
    }
  } catch {
    // WebGL not available
  }

  // Touch support
  components.push(String(navigator.maxTouchPoints || 0));

  // Connection type
  const conn = (navigator as any).connection;
  if (conn) {
    components.push(conn.effectiveType || '');
  }

  // Hash it
  const raw = components.join('|');
  const encoder = new TextEncoder();
  const data = encoder.encode(raw);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const getGpuRenderer = (): string | null => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || null;
      }
    }
  } catch {
    // noop
  }
  return null;
};

/**
 * Fires once per session to identify the visitor via fingerprinting + IP enrichment.
 * Deferred via requestIdleCallback so it never impacts page performance.
 */
export const useIdentityResolution = () => {
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;

    // Check if already sent this session
    if (sessionStorage.getItem('obc_identity_sent')) {
      sentRef.current = true;
      return;
    }

    const run = async () => {
      sentRef.current = true;
      sessionStorage.setItem('obc_identity_sent', '1');

      try {
        const fingerprint = await generateFingerprint();
        const utmParams = getStoredUtmParams();
        const conn = (navigator as any).connection;

        const urlParams = new URLSearchParams(window.location.search);

        const payload = {
          visitor_id: localStorage.getItem('obc_visitor_id') || '',
          session_id: sessionStorage.getItem('obc_session_id') || '',
          fingerprint_hash: fingerprint,
          screen_resolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          platform: navigator.platform || '',
          gpu_renderer: getGpuRenderer(),
          connection_type: conn?.effectiveType || null,
          page_url: window.location.href,
          page_path: window.location.pathname,
          referrer: document.referrer || null,
          utm_source: utmParams.utm_source || null,
          utm_medium: utmParams.utm_medium || null,
          utm_campaign: utmParams.utm_campaign || null,
          fbclid: urlParams.get('fbclid') || getCookie('_fbc') || null,
          gclid: urlParams.get('gclid') || getCookie('_gcl_aw') || null,
        };

        await fetch(LOCAL_FUNCTIONS.identifyVisitor, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch {
        // Identity resolution is best-effort; never break the app
        console.debug('[IdentityResolution] Failed to send fingerprint');
      }
    };

    // Defer until browser is idle
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => run(), { timeout: 5000 });
    } else {
      setTimeout(run, 3000);
    }
  }, []);
};

export default useIdentityResolution;
